import { useReportsStore } from "@/_store/reports.module";
import { storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import type { TimeFrame } from "@/types";
import moment from "moment/moment";
import {
  getNinetyDayReportsDateRangePresets,
  getReportsDefaultDateRangePresets,
  getReportsLiveWindowFloor,
  getSatReportsDateRangePresets,
  ReportType,
} from "@/constants/reports";
import { getCurrentDate, getNDaysAgo } from "@/constants/date-range-picker";
import isEqual from "lodash/isEqual";
import { useRoute } from "vue-router";
import { appLogoDefaultPath, useWorkspaceStore } from "@/_store/workspace.module.ts";

export function useReportsTab(reportType: ReportType, defaultDayPeriod: number = 30) {
  const defaultFiltersState = {
    showZeroValues: false,
    reportTimeFrame: {
      start: getNDaysAgo(defaultDayPeriod - 1),
      end: getCurrentDate(),
    },
  };

  const route = useRoute();
  const workspaceStore = useWorkspaceStore();
  const reportsStore = useReportsStore();
  const { appLogo } = storeToRefs(workspaceStore);
  const {
    executiveSummaryReport,
    dnsSummaryReport,
    securedMessagesSummaryReport,
    managedServicesSummaryReport,
    warningOnlyEmailEventsReport,
    warningOnlyEmailEventsMspReport,
    loading,
    reportVersion,
  } = storeToRefs(reportsStore);
  const reportCallbackMap = {
    [ReportType.EXECUTIVE_SUMMARY]: reportsStore.getExecutiveSummaryReport,
    [ReportType.SECURED_MESSAGES]: reportsStore.getSecuredMessagesSummaryReport,
    [ReportType.MANAGED_SERVICES_SUMMARY]: reportsStore.getManagedServicesSummaryReport,
    [ReportType.DNS_SUMMARY]: reportsStore.getDnsReport,
    [ReportType.PHISHING_SIMULATION]: reportsStore.getPhishingSimulationReport,
    [ReportType.SECURITY_AWARENESS]: reportsStore.getSecurityAwarenessReport,
    [ReportType.WARNING_ONLY_EMAIL_EVENTS]: reportsStore.getWarningOnlyEmailEventsReport,
    [ReportType.WARNING_ONLY_EMAIL_EVENTS_MSP]: reportsStore.getWarningOnlyEmailEventsMspReport,
  } as const;

  const getReportCallback = reportCallbackMap[reportType];

  const localFilters = ref<{ reportTimeFrame: TimeFrame; showZeroValues: boolean }>({
    ...defaultFiltersState,
  });

  const logoPath = computed(() => {
    // MSP-level report always uses the large primary Coro logo
    if (reportType === ReportType.WARNING_ONLY_EMAIL_EVENTS_MSP) {
      return "/images/logos/coro-logo-large-primary.svg";
    }
    return appLogo.value === appLogoDefaultPath
      ? "/images/logos/coro-logo-vertical.svg"
      : appLogo.value;
  });

  const formattedSelectedDateRange = computed(() => {
    const { start, end } = momentTimeFrame.value;
    return `${start.format("ll")} - ${end.format("ll")}`;
  });

  const customerName = computed(() => workspaceStore.workspace.name);

  const momentTimeFrame = computed(() => {
    const { end, start } = localFilters.value.reportTimeFrame;
    const timeframeStart = start ? moment(start) : moment(getNDaysAgo(defaultDayPeriod - 1));
    const timeframeEnd = end ? moment(end) : moment();
    return {
      start: timeframeStart,
      end: timeframeEnd,
    };
  });

  const showClearFiltersButton = computed(() => {
    return !isEqual(localFilters.value, defaultFiltersState);
  });

  const resetFilters = () => {
    localFilters.value = { ...defaultFiltersState };
  };

  onMounted(async () => {
    const { start } = route.query;
    const startValid = start && moment(Number.parseInt(start as string)).isValid();

    const formattedStart = startValid
      ? moment(Number.parseInt(start as string)).format("YYYY-MM-DD")
      : momentTimeFrame.value.start.format("YYYY-MM-DD");

    if (startValid) {
      localFilters.value.reportTimeFrame.start = formattedStart;
    }

    reportsStore.$patch({
      selectedTimeFrame: {
        start: formattedStart,
        end: momentTimeFrame.value.end.format("YYYY-MM-DD"),
      },
    });
    await getReportCallback();
  });

  onUnmounted(() => {
    reportsStore.$patch({ selectedTimeFrame: undefined });
  });

  watch(
    () => localFilters.value.reportTimeFrame,
    async () => {
      reportsStore.$patch({
        selectedTimeFrame: {
          start: momentTimeFrame.value.start.format("YYYY-MM-DD"),
          end: momentTimeFrame.value.end.format("YYYY-MM-DD"),
        },
      });
      await getReportCallback();
    }
  );

  // Reports whose data only goes back 90 days. The Executive summary is absent on purpose: its
  // range stays unlimited and it reads the archive for anything older.
  const isNinetyDayReport =
    reportType === ReportType.WARNING_ONLY_EMAIL_EVENTS ||
    reportType === ReportType.WARNING_ONLY_EMAIL_EVENTS_MSP ||
    reportType === ReportType.MANAGED_SERVICES_SUMMARY;

  /** Undefined for the reports that stay unlimited, so their calendar keeps no lower bound. */
  const minDate = computed(() => (isNinetyDayReport ? getReportsLiveWindowFloor() : undefined));

  // End dates in the future describe a period that has not happened.
  const maxDate = computed(() => getCurrentDate());

  const presets = computed(() => {
    if (isNinetyDayReport) {
      return getNinetyDayReportsDateRangePresets();
    }

    const isSatReport =
      reportType === ReportType.SECURITY_AWARENESS || reportType === ReportType.PHISHING_SIMULATION;

    if (isSatReport) {
      return getSatReportsDateRangePresets();
    }

    // Default fallback
    return getReportsDefaultDateRangePresets();
  });

  return {
    customerName,
    executiveSummaryReport,
    securedMessagesSummaryReport,
    managedServicesSummaryReport,
    warningOnlyEmailEventsReport,
    warningOnlyEmailEventsMspReport,
    dnsSummaryReport,
    loading,
    logoPath,
    localFilters,
    formattedSelectedDateRange,
    minDate,
    maxDate,
    presets,
    reportVersion,
    momentTimeFrame,
    showClearFiltersButton,
    resetFilters,
  };
}
